import React, { useRef, useState } from 'react';
import { FileUp, Link2, Loader2, X } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useRegisterTopEscapeClose } from '../hooks/useEscapeCloseStack';
import { extractListingImport } from '../services/listingImportService';
import { listingPreviewRows, convertListingPerUnitExpenses } from '../utils/applyListingImport';

const MAX_FILE_BYTES = 8 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const res = String(r.result || '');
      const idx = res.indexOf('base64,');
      resolve(idx >= 0 ? res.slice(idx + 7) : res);
    };
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(file);
  });
}

function sanitizeFields(fields) {
  const next = { ...(fields || {}) };
  if (next.marketingDescriptionHtml) {
    next.marketingDescriptionHtml = DOMPurify.sanitize(String(next.marketingDescriptionHtml), {
      USE_PROFILES: { html: true },
    });
  }
  return next;
}

function previewLabel(key, t) {
  const map = {
    address: 'basicInfo.address',
    city: 'basicInfo.city',
    province: 'basicInfo.province',
    postalCode: 'basicInfo.postalCode',
    numberOfUnits: 'basicInfo.numberOfUnits',
    askingPrice: 'basicInfo.askingPrice',
    purchasePrice: 'basicInfo.purchasePrice',
    annualRent: 'revenue.annualRent',
    parkingRevenue: 'revenue.parkingRevenue',
    vacancyRate: 'revenue.vacancyRate',
    municipalTaxes: 'operatingExpenses.municipalTaxes',
    schoolTaxes: 'operatingExpenses.schoolTaxes',
    insurance: 'operatingExpenses.insurance',
    heating: 'operatingExpenses.heating',
    electricity: 'operatingExpenses.electricity',
    electricityHeating: 'operatingExpenses.electricityHeating',
    operatingExpenses: 'operatingExpenses.totalExpenses',
    maintenance: 'operatingExpenses.maintenancePerUnit',
    concierge: 'operatingExpenses.conciergePerUnit',
    managementRate: 'operatingExpenses.managementRate',
  };
  return t(map[key] || key);
}

export default function ListingImportModal({ open, onClose, onApply, t, language = 'fr' }) {
  const [listingUrl, setListingUrl] = useState('');
  const [listingText, setListingText] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [urlFetchStatus, setUrlFetchStatus] = useState('');
  const [fields, setFields] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [advancedExpenses, setAdvancedExpenses] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setListingUrl('');
    setListingText('');
    setFile(null);
    setLoading(false);
    setError('');
    setUrlFetchStatus('');
    setFields(null);
    setWarnings([]);
    setAdvancedExpenses(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useRegisterTopEscapeClose(handleClose, open);

  if (!open) return null;

  const handleExtract = async () => {
    setError('');
    const url = listingUrl.trim();
    const text = listingText.trim();
    if (!url && !text && !file) {
      setError(t('listingImport.errorNeedSource'));
      return;
    }
    if (file && file.size > MAX_FILE_BYTES) {
      setError(t('listingImport.errorFileTooLarge'));
      return;
    }
    setLoading(true);
    try {
      let fileBase64;
      if (file) {
        fileBase64 = await fileToBase64(file);
      }
      const data = await extractListingImport({
        listingUrl: url,
        listingText: text,
        fileBase64,
        fileName: file?.name,
        locale: language,
      });
      setUrlFetchStatus(data.urlFetchStatus || '');
      if (data.needsMoreContent) {
        setFields(null);
        setWarnings([]);
        setError(t('listingImport.needsMoreContent'));
        return;
      }
      const cleaned = convertListingPerUnitExpenses(sanitizeFields(data.fields || {}));
      if (!Object.keys(cleaned).length) {
        setError(t('listingImport.errorNoFields'));
        return;
      }
      setFields(cleaned);
      setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setAdvancedExpenses(!!data.advancedExpenses);
    } catch (e) {
      const msg = e?.message || String(e);
      const code = e?.code || '';
      if (/resource-exhausted|Limite quotidienne/i.test(msg)) {
        setError(t('listingImport.errorQuota'));
      } else if (/unauthenticated/i.test(msg) || code === 'unauthenticated') {
        setError(t('listingImport.errorAuth'));
      } else if (
        code === 'internal' ||
        /internal/i.test(msg) ||
        /failed to fetch|network|cors|access-control|ERR_FAILED/i.test(msg)
      ) {
        setError(t('listingImport.errorNetwork'));
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!fields) return;
    onApply({
      fields,
      warnings,
      advancedExpenses,
      sourceUrl: listingUrl.trim(),
    });
    reset();
  };

  const rows = fields ? listingPreviewRows(fields) : [];
  const fetchNote =
    urlFetchStatus === 'blocked'
      ? t('listingImport.fetchBlocked')
      : urlFetchStatus === 'failed'
        ? t('listingImport.fetchFailed')
        : urlFetchStatus === 'ok'
          ? t('listingImport.fetchOk')
          : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="listing-import-modal-title"
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-2">
          <div>
            <h2 id="listing-import-modal-title" className="text-xl font-semibold text-gray-900">
              {t('listingImport.title')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">{t('listingImport.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label={t('cancel')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pb-6 pt-2 overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1" htmlFor="listing-import-url">
              {t('listingImport.urlLabel')}
            </label>
            <div className="relative">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
              <input
                id="listing-import-url"
                type="url"
                value={listingUrl}
                onChange={(e) => setListingUrl(e.target.value)}
                disabled={loading}
                placeholder={t('listingImport.urlPlaceholder')}
                className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('listingImport.urlHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1" htmlFor="listing-import-text">
              {t('listingImport.textLabel')}
            </label>
            <textarea
              id="listing-import-text"
              value={listingText}
              onChange={(e) => setListingText(e.target.value)}
              disabled={loading}
              rows={6}
              placeholder={t('listingImport.textPlaceholder')}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div>
            <p className="block text-sm font-medium text-gray-800 mb-1">{t('listingImport.fileLabel')}</p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.txt,.docx,.html,.htm,application/pdf,text/plain"
                disabled={loading}
                className="text-sm"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file ? (
                <span className="text-xs text-gray-600">{file.name}</span>
              ) : (
                <span className="text-xs text-gray-500">{t('listingImport.fileHint')}</span>
              )}
            </div>
          </div>

          {error ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              {error}
            </div>
          ) : null}
          {!error && fetchNote && fields ? (
            <p className="text-xs text-gray-600">{fetchNote}</p>
          ) : null}

          {fields ? (
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('listingImport.previewTitle')}</h3>
              <p className="text-xs text-gray-600 mb-3">{t('listingImport.previewHint')}</p>
              {rows.length > 0 ? (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {rows.map((row) => (
                    <div key={row.key} className="flex justify-between gap-2 border-b border-gray-100 py-1">
                      <dt className="text-gray-600">{previewLabel(row.key, t)}</dt>
                      <dd className="font-medium text-gray-900 text-right break-all">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="text-sm text-gray-600">{t('listingImport.previewEmpty')}</p>
              )}
              {warnings.length > 0 ? (
                <ul className="mt-3 list-disc pl-5 text-xs text-amber-900 space-y-1">
                  {warnings.map((w, i) => (
                    <li key={`${i}-${w.slice(0, 24)}`}>{w}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t bg-white flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={handleExtract}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
            {loading ? t('listingImport.extracting') : t('listingImport.extract')}
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!fields || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-60"
          >
            {t('listingImport.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
